/** Parse rial fields from discount inquiry (clinic or APIGW). PHP often sends `"null"` as a string. */
export function parseDiscountMoney(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'null' || trimmed === 'undefined' || trimmed === 'nan') return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export type DiscountInquiryPayload = {
  status?: number;
  message?: string;
  result?: DiscountInquiryResult | null;
  data?: DiscountInquiryPayload;
};

export type DiscountInquiryResult = {
  payable_cost?: unknown;
  discount_price?: unknown;
  vat?: unknown;
  token?: unknown;
  percent?: unknown;
  max_price?: unknown;
  service_price?: unknown;
  price?: unknown;
};

export function unwrapDiscountInquiryPayload(data: unknown): DiscountInquiryPayload {
  if (!data || typeof data !== 'object') return {};
  const body = data as DiscountInquiryPayload;
  if (typeof body.status === 'number' || body.result != null) return body;
  if (body.data && typeof body.data === 'object') return unwrapDiscountInquiryPayload(body.data);
  return body;
}

function vatOnRemainingService(originalVat: number | undefined, servicePrice: number, remainingService: number) {
  if (remainingService <= 0) return 0;
  if (originalVat == null || servicePrice <= 0) return originalVat ?? 0;
  return Math.round((originalVat * remainingService) / servicePrice);
}

export function mapDiscountInquirySuccess(result: DiscountInquiryResult | undefined | null, code: string) {
  const servicePrice = parseDiscountMoney(result?.service_price);
  const grossPrice = parseDiscountMoney(result?.price);
  let vat = parseDiscountMoney(result?.vat);
  const percent = parseDiscountMoney(result?.percent);
  const maxPrice = parseDiscountMoney(result?.max_price);
  let discount = parseDiscountMoney(result?.discount_price);
  let payable = parseDiscountMoney(result?.payable_cost);
  const token = typeof result?.token === 'string' && result.token.trim() ? result.token : undefined;

  if (discount == null && percent != null && percent > 0 && servicePrice != null) {
    discount = Math.round((servicePrice * percent) / 100);
    if (maxPrice != null && maxPrice >= 0) {
      discount = Math.min(discount, maxPrice);
    }
  }

  const remainingService = servicePrice != null && discount != null ? Math.max(0, servicePrice - discount) : undefined;
  const basePayable = grossPrice ?? (servicePrice != null ? servicePrice + (vat ?? 0) : undefined);
  const discountLooksUnapplied =
    discount != null &&
    discount > 0 &&
    (payable == null || (basePayable != null && payable === basePayable) || remainingService === 0);

  // Clinic historically returned payable_cost=0 for 100% (tax not charged). Recalculate VAT on remaining service.
  if (discountLooksUnapplied && remainingService != null && servicePrice != null) {
    vat = vatOnRemainingService(vat, servicePrice, remainingService);
    payable = remainingService + vat;
  }

  return {
    ...(payable != null ? { totalPrice: String(payable) } : {}),
    ...(discount != null && discount > 0 ? { discount: String(discount) } : {}),
    ...(vat != null ? { tax: String(vat) } : {}),
    ...(token ? { discountToken: token } : {}),
    isValidDiscount: true as const,
    code,
  };
}
